import { useState } from "react";
import { Link } from "wouter";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { RelatedPages } from "@/components/RelatedPages";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { NetworkRouter } from "@/components/NetworkRouter";
import { breadcrumbSchema, personSchema, webPageSchema } from "@/lib/seo";
import { withNetworkUtm } from "@/lib/network";

import { EMAILS } from "@/lib/emails";
const SUPPORT_EMAIL = EMAILS.notifications;

const CATEGORIES: { value: string; label: string; description: string }[] = [
  {
    value: "books",
    label: "Books",
    description: "New books, new editions, pre-orders, launch days.",
  },
  {
    value: "essays",
    label: "Essays",
    description: "New writing on this site.",
  },
  {
    value: "shrinq",
    label: "shrinQ",
    description:
      "Updates on the program, including the day it opens for purchase.",
  },
  {
    value: "shrinkmd",
    label: "shrinkMD",
    description: "State expansions, intake changes, clinical updates.",
  },
  {
    value: "network",
    label: "The Shrink Network",
    description: "New properties, major site launches, ecosystem updates.",
  },
  {
    value: "speaking",
    label: "Speaking and media",
    description:
      "When I'm on something worth listening to, or speaking somewhere worth attending.",
  },
];

export default function Notifications() {
  const [submitted, setSubmitted] = useState(false);

  const TITLE = "Notifications | Shariq Refai, MD, MBA";
  const DESCRIPTION =
    "Get notified when Shariq Refai, MD, MBA publishes a new essay, book, or Shrink Network update. Small list. No marketing. Unsubscribe any time.";

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Notifications", path: "/notifications" },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") || "").trim();
    const interests = fd.getAll("interests").map(String).join(", ");

    try {
      const r = await fetch("/api/notifications", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email,
          interests: interests || "All",
          _hp: String(fd.get("_hp") || ""),
        }),
      });
      if (r.ok) {
        setSubmitted(true);
        return;
      }
    } catch {
      // Fall through to mailto fallback below.
    }

    // Fallback if backend is unreachable or not yet configured.
    const subject = "[shariqrefai.com] Notifications signup";
    const body = [
      `Email: ${email}`,
      `Interests: ${interests || "All"}`,
      "",
      "Please add me to the notifications list.",
    ].join("\n");
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  return (
    <SiteLayout>
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        url="/notifications"
        schema={[
          personSchema,
          webPageSchema({
            path: "/notifications",
            title: TITLE,
            description: DESCRIPTION,
            breadcrumbs: crumbs,
          }),
          breadcrumbSchema(crumbs),
        ]}
      />

      <article className="container mx-auto max-w-2xl px-4 md:px-8 pt-24 pb-16 md:pt-32 md:pb-24">
        <Breadcrumbs items={crumbs.map((c) => ({ name: c.name, href: c.path }))} />
        <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
          Notifications
        </span>
        <h1 className="text-4xl md:text-5xl font-serif text-primary leading-tight mt-6 mb-4">
          Book &amp; Essay Notifications from Shariq Refai
        </h1>
        <p className="text-lg text-secondary font-light leading-relaxed mb-10">
          New books, new sites, new essays, new launches. One list. One email
          when it matters.
        </p>

        <div className="space-y-5 text-secondary font-light leading-relaxed mb-10">
          <p>
            I don't run a newsletter. I don't write weekly. When I do publish
            something new (a book, an essay, a launch, a network update), I send
            one email about it to the people who asked to know. No marketing
            sequence. No upsell. One email per thing.
          </p>
          <p>
            If you want any of the following, drop your email below and pick the
            categories that fit.
          </p>
        </div>

        {submitted ? (
          <div
            role="status"
            aria-live="polite"
            className="border border-border bg-muted/40 p-8"
          >
            <h2 className="font-serif text-2xl text-primary mb-3">
              Got it.
            </h2>
            <p className="text-secondary font-light leading-relaxed">
              I'll only email when there's something worth the inbox. Your email
              client should have opened with a pre-filled message. Send it to
              finish signing up. If nothing opened, email{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                {SUPPORT_EMAIL}
              </a>{" "}
              directly.
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-6 border border-border bg-card p-8"
          >
            {/* Honeypot: real users don't fill this; bots do. Server drops silently. */}
            <input
              type="text"
              name="_hp"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="sr-only"
              style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}
            />
            <fieldset className="flex flex-col gap-4">
              <legend className="text-sm font-medium text-primary mb-1">
                Pick the categories that fit
              </legend>
              {CATEGORIES.map((c) => (
                <label
                  key={c.value}
                  className="flex items-start gap-3 text-sm text-secondary font-light"
                >
                  <input
                    type="checkbox"
                    name="interests"
                    value={c.value}
                    className="h-4 w-4 mt-1 accent-accent"
                  />
                  <span>
                    <span className="text-primary font-medium">{c.label}.</span>{" "}
                    {c.description}
                  </span>
                </label>
              ))}
            </fieldset>

            <div className="flex flex-col gap-2">
              <label htmlFor="email" className="text-sm font-medium text-primary">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                required
                aria-required="true"
                placeholder="you@yourname.com"
                className="h-12 px-4 border border-input bg-white text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background focus:border-primary transition-all"
              />
            </div>

            <button
              type="submit"
              className="h-12 bg-primary text-primary-foreground font-medium hover:bg-secondary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Sign up
            </button>

            <p className="text-xs text-muted-foreground font-light italic leading-relaxed">
              Your email goes to me, not to a marketing list. Unsubscribe in one
              click.
            </p>
          </form>
        )}

        <p className="mt-12 text-sm text-muted-foreground font-light leading-relaxed">
          For clinical care, please visit{" "}
          <a
            href={withNetworkUtm("https://shrinkmd.com/", "notifications_page")}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
          >
            shrinkmd.com
          </a>
          . For media or speaking inquiries, use the{" "}
          <Link
            href="/contact"
            className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
          >
            contact form
          </Link>
          .
        </p>
      </article>

      {/* Continue */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-secondary text-white">
        <div className="container mx-auto max-w-2xl">
          <NetworkRouter
            variant="dark"
            eyebrow="Continue"
            cards={[
              { label: "Read the founder narrative", href: "/founder-of-the-shrink-network" },
              { label: "See the project list", href: "/projects" },
              { label: "Read the books", href: "/books" },
              { label: "Read the latest perspectives", href: "/perspectives" },
            ]}
          />
        </div>
      </section>
      <RelatedPages
        entries={[
          { href: "/perspectives", label: "Perspectives essays", why: "The current library of essays you'll be notified about." },
          { href: "/books", label: "Books by Shariq Refai", why: "The three books in progress and pre-order updates." },
          { href: "/what-im-building", label: "What I'm building", why: "The upcoming projects across the network." },
        ]}
      />
    </SiteLayout>
  );
}
